// Setup → Recycle Bin: /api/recycle-bin (apps/server/src/recycleBin.mjs).

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

export const RECYCLE_BIN_API = '/api/recycle-bin';

/**
 * How long a deleted record stays restorable, for delete confirmations. The
 * server's RECYCLE_BIN_DAYS decides; recycleBin.db.test.mjs keeps them equal.
 */
export const RECYCLE_BIN_DAYS = 30;

/** Said after a delete confirmation: where the record goes. */
export const restoreNote = (count = 1): string =>
  `You can restore ${count === 1 ? 'it' : 'them'} from the Recycle Bin for ${RECYCLE_BIN_DAYS} days.`;

export interface BinItem {
  id: string;
  /** The API object key (accounts, quotes, …). */
  object: string;
  objectLabel: string;
  recordId: string;
  name: string;
  /** The record and every row its delete took with it. */
  rowCount: number;
  deletedBy: string;
  deletedAt: string;
  purgeAt: string;
  daysLeft: number;
}

export interface BinList {
  retentionDays: number;
  canEmpty: boolean;
  items: BinItem[];
}

export interface RestoreResult {
  restored: { id: string; object: string; recordId: string }[];
  failed: { id: string; error: string }[];
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const str = (value: unknown): string => (typeof value === 'string' ? value : '');
const num = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

async function read(response: Response, fallback: string): Promise<unknown> {
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) throw new Error(isObject(body) && typeof body.error === 'string' ? body.error : fallback);
  return body;
}

export function parseBinList(body: unknown): BinList {
  if (!isObject(body) || !Array.isArray(body.items)) throw new Error('The server sent a Recycle Bin the page cannot read.');
  return {
    retentionDays: num(body.retentionDays) || RECYCLE_BIN_DAYS,
    canEmpty: body.canEmpty === true,
    items: body.items.filter(isObject).map((item) => ({
      id: str(item.id),
      object: str(item.object),
      objectLabel: str(item.objectLabel),
      recordId: str(item.recordId),
      name: str(item.name),
      rowCount: num(item.rowCount),
      deletedBy: str(item.deletedBy),
      deletedAt: str(item.deletedAt),
      purgeAt: str(item.purgeAt),
      daysLeft: num(item.daysLeft),
    })),
  };
}

const post = (fetcher: Fetch, path: string, body?: unknown) =>
  fetcher(`${RECYCLE_BIN_API}${path}`, {
    method: 'POST',
    ...(body === undefined ? {} : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  });

export async function listBin(fetcher: Fetch): Promise<BinList> {
  return parseBinList(await read(await fetcher(RECYCLE_BIN_API), 'Could not load the Recycle Bin.'));
}

export async function restoreEntries(fetcher: Fetch, ids: string[]): Promise<RestoreResult> {
  const body = await read(await post(fetcher, '/restore', { ids }), 'Could not restore.');
  const list = (value: unknown) => (Array.isArray(value) ? value.filter(isObject) : []);
  return {
    restored: isObject(body) ? list(body.restored).map((r) => ({ id: str(r.id), object: str(r.object), recordId: str(r.recordId) })) : [],
    failed: isObject(body) ? list(body.failed).map((f) => ({ id: str(f.id), error: str(f.error) })) : [],
  };
}

export async function deleteEntries(fetcher: Fetch, ids: string[]): Promise<number> {
  const body = await read(await post(fetcher, '/delete', { ids }), 'Could not delete.');
  return isObject(body) ? num(body.deleted) : 0;
}

export async function emptyBin(fetcher: Fetch): Promise<number> {
  const body = await read(await post(fetcher, '/empty'), 'Could not empty the Recycle Bin.');
  return isObject(body) ? num(body.deleted) : 0;
}
